import { ModelContract, ModuleDescription } from "../../shared/module-description";
import { createEffect, createEvent, createStore, sample } from "effector";
import { reset } from "patronum";
import { ICurrentTopic } from "../../shared/entities/enrollments";
import { routePathNameChanged } from "../../app/model";
import { IUserAuthContract } from "../../student/auth/model";
import { $topics, ITopicsContract } from "../../topics/model";
import { IEnrollmentsContract } from "../../enrollments/model";
import { TopicStarted } from "@features/shared/events";
import { $currentCourse, trackRelatedCoursesShownFx } from "@features/courses/ui/current";

export const currentTopicChanged = createEvent<ICurrentTopic | null>();
export const currentTopicIdChanged = createEvent<number | null>();
const setSimilarCourseInTopicModalVisible = createEvent<boolean>();

export const $currentTopic = createStore<ICurrentTopic | null>(null);

export const $currentTopicId = createStore<number | null>(null);
export const $isSimilarCoursesInTopicModalOpen = createStore(false)
    .on(setSimilarCourseInTopicModalVisible, (_, p) => p)
    .reset(routePathNameChanged);

export const trackTopicStartedFx = createEffect<{ courseId: number; topicId: number; title: string }, void>(
    ({ courseId, topicId, title }) => {
        TopicStarted(courseId, topicId, title);
    }
);

sample({
    clock: currentTopicChanged,
    target: $currentTopic
});

sample({
    clock: currentTopicIdChanged,
    target: $currentTopicId
});

sample({
    source: { topics: $topics, currentCourse: $currentCourse },
    filter: ({ currentCourse }) => currentCourse?.type === "challenge",
    fn: ({ topics }) => topics[0].id,
    target: currentTopicIdChanged
});

sample({
    clock: $isSimilarCoursesInTopicModalOpen,
    source: {
        currentCourse: $currentCourse,
        isSimilarCoursesModalVisible: $isSimilarCoursesInTopicModalOpen
    },
    filter: ({ isSimilarCoursesModalVisible, currentCourse }) => isSimilarCoursesModalVisible && currentCourse !== null,
    fn: ({ currentCourse }) => ({
        courseId: currentCourse?.id ?? 0,
        courseType: currentCourse?.type ?? "course",
        placement: "level_complete"
    }),
    target: trackRelatedCoursesShownFx
});

const contract = new ModelContract(
    {
        $currentTopic,
        $currentTopicId,
        $isSimilarCoursesInTopicModalOpen
    },
    {
        currentTopicChanged,
        currentTopicIdChanged,
        setSimilarCourseInTopicModalVisible
    },
    {},
    {}
);

export type ICurrentTopicContract = typeof contract;

export class CurrentTopicModule extends ModuleDescription<ICurrentTopicContract> {
    constructor(
        public userAuthModule: IUserAuthContract,
        public topicsModule: ITopicsContract,
        public enrollmentsModule: IEnrollmentsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: routePathNameChanged,
            filter: (url: string) => url.match(/\/topic\/(\d+)/) !== null,
            fn: (url) => {
                const match = url.match(/\/topic\/(\d+)/);
                return match ? Number(match[1]) : null;
            },
            target: currentTopicIdChanged
        });

        sample({
            clock: [
                currentTopicIdChanged,
                this.topicsModule.events.topicsLoaded,
                this.enrollmentsModule.events.enrollTopicsLoadingFinished
            ],
            source: {
                topicId: $currentTopicId,
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                topics: this.topicsModule.stores.$topics
            },
            filter: ({ topicId, topics }) => {
                return topicId !== null && topics.length > 0;
            },
            fn: ({ topicId, enrollmentsTopics, topics }) => {
                const topic = topics.find((topic) => topic.id === topicId);
                if (!topic) return null;

                const enrollmentTopic = enrollmentsTopics?.find(
                    (enrollmentTopic) => enrollmentTopic.topic_id === topicId
                );
                return enrollmentTopic ? { ...enrollmentTopic, ...topic } : topic;
            },
            target: currentTopicChanged
        });

        sample({
            clock: routePathNameChanged,
            filter: (url: string) => url.match(/\/topic\/(\d+)/) !== null,
            fn: (url) => {
                const match = url.match(/\/topic\/(\d+)/);
                return match ? Number(match[1]) : null;
            },
            target: currentTopicIdChanged
        });

        reset({
            clock: this.userAuthModule.events.logoutStudent,
            target: $currentTopic
        });
    }
}
